import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // Load cart from backend on initialization (if authenticated)
  const loadCart = useCallback(async () => {
    try {
      setLoading(true);
      const res = await cartApi.getCartItems();
      const items = res.data?.items ?? res.data ?? [];
      setCartItems(items);
    } catch (err) {
      console.error('Failed to load cart (context):', err);
      // toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
  if (isAuthenticated) {
    loadCart();
  } 
}, [isAuthenticated, loadCart]);


  // Add course to cart. If not authenticated store pending intent.
  const addToCart = useCallback(async (courseId) => {
    if (!isAuthenticated) {
      // persist intent and let UI redirect to login flow
      localStorage.setItem('pending_add_to_cart', String(courseId));
      toast.success('Please login to complete adding course to cart');
      return;
    }

    try {
      setLoading(true);
      await cartApi.addToCart(courseId);
      // Refresh cart items
      const res = await cartApi.getCartItems();
      const items = res.data?.items ?? res.data ?? [];
      setCartItems(items);
      toast.success('Course added to cart');
    } catch (err) {
      console.error('Error adding to cart:', err);
      toast.error('Failed to add course to cart');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Helper to check if a course is already in cart.
  // Matches against both the MongoDB ObjectId (item.id) and the public
  // course_number so callers can pass either identifier.
  const isInCart = useCallback((courseId) => {
    return cartItems.some(
      item =>
        String(item.id) === String(courseId) ||
        String(item.course_number) === String(courseId)
    );
  }, [cartItems]);

  // Return number of items in cart (used by Navbar)
  const getCartCount = useCallback(() => {
    return cartItems?.length ?? 0;
  }, [cartItems]);

  // remove from local cart state (used by pages after successful API remove)
  const removeFromCart = useCallback((courseId) => {
    setCartItems(prev => prev.filter(item => String(item.id) !== String(courseId)));
  }, []);

  // clear cart helper
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const value = {
    cartItems,
    loading,
    setCartItems,
    addToCart,
    isInCart,
    getCartCount,
    removeFromCart,
    clearCart,
    loadCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;